// SubscriptionContext.js
// Provides subscription state management across the application
// Listens to real-time updates from Firestore for subscription changes

import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';
import {
  PLAN_TYPES,
  SUBSCRIPTION_STATUS,
  DEFAULT_SUBSCRIPTION,
  PLAN_LIMITS,
  canUsePlanFeature,
  getPlanLimits,
  getRequiredPlanForFeature,
  calculateTrialDaysRemaining,
} from '../config/subscriptionConfig';

const SubscriptionContext = createContext();

export function SubscriptionProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const [subscription, setSubscription] = useState(DEFAULT_SUBSCRIPTION);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Listen to user document for subscription changes
  useEffect(() => {
    // Wait for auth to initialize
    if (authLoading) {
      return;
    }

    // No user = reset to default subscription
    if (!user) {
      setSubscription(DEFAULT_SUBSCRIPTION);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // Subscribe to user document
    const unsubscribe = onSnapshot(
      doc(db, 'users', user.uid),
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const userData = docSnapshot.data();
          // Use subscription data from user document, or default if not set
          const subscriptionData = userData.subscription || DEFAULT_SUBSCRIPTION;
          setSubscription(subscriptionData);
        } else {
          // User document doesn't exist yet (new user)
          setSubscription(DEFAULT_SUBSCRIPTION);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching subscription:', err);
        setError(err.message);
        setSubscription(DEFAULT_SUBSCRIPTION);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user, authLoading]);

  // Check if user has a specific plan or higher
  const isPro = useMemo(() => {
    return subscription.plan === PLAN_TYPES.PRO || subscription.plan === PLAN_TYPES.LEAGUE;
  }, [subscription.plan]);

  const isLeague = useMemo(() => {
    return subscription.plan === PLAN_TYPES.LEAGUE;
  }, [subscription.plan]);

  const isFree = useMemo(() => {
    return subscription.plan === PLAN_TYPES.FREE;
  }, [subscription.plan]);

  // Check subscription status
  const isTrialing = useMemo(() => {
    return subscription.status === SUBSCRIPTION_STATUS.TRIALING;
  }, [subscription.status]);

  const isActive = useMemo(() => {
    return subscription.status === SUBSCRIPTION_STATUS.ACTIVE || 
           subscription.status === SUBSCRIPTION_STATUS.TRIALING;
  }, [subscription.status]);

  const isPastDue = useMemo(() => {
    return subscription.status === SUBSCRIPTION_STATUS.PAST_DUE;
  }, [subscription.status]);

  const isCanceled = useMemo(() => {
    return subscription.status === SUBSCRIPTION_STATUS.CANCELED;
  }, [subscription.status]);

  // Calculate trial days remaining
  const trialDaysRemaining = useMemo(() => {
    if (!isTrialing || !subscription.trialEnd) return 0;
    return calculateTrialDaysRemaining(subscription.trialEnd);
  }, [isTrialing, subscription.trialEnd]);

  // Check if trial is expiring soon (3 days or less)
  const isTrialExpiringSoon = useMemo(() => {
    return isTrialing && trialDaysRemaining > 0 && trialDaysRemaining <= 3;
  }, [isTrialing, trialDaysRemaining]);

  // Get current plan limits
  const currentLimits = useMemo(() => {
    return getPlanLimits(subscription.plan);
  }, [subscription.plan]);

  // Check if user can use a specific feature
  const canUseFeature = useCallback((feature) => {
    return canUsePlanFeature(subscription.plan, feature);
  }, [subscription.plan]);

  // Get the required plan for a feature
  const getRequiredPlan = useCallback((feature) => {
    return getRequiredPlanForFeature(feature);
  }, []);

  // Check if user is within a limit
  const isWithinLimit = useCallback((limitType, currentValue) => {
    const limits = currentLimits;
    const limit = limits[limitType];
    
    if (limit === Infinity) return true;
    return currentValue < limit;
  }, [currentLimits]);

  // Check if subscription will cancel at period end
  const willCancelAtPeriodEnd = useMemo(() => {
    return subscription.cancelAtPeriodEnd === true;
  }, [subscription.cancelAtPeriodEnd]);

  // ============ Legacy methods for Dashboard compatibility ============
  
  // Check if user has an active subscription (not free, or in trial/active)
  const hasActiveSubscription = useCallback(() => {
    return isActive && !isFree;
  }, [isActive, isFree]);

  // Check if user can access league features
  const canAccessLeagues = useCallback(() => {
    // All plans (including free) can access leagues
    // Free has limits enforced elsewhere
    return true;
  }, []);

  // Check if user can access standalone tournament features
  const canAccessTournaments = useCallback(() => {
    // All plans can access tournaments
    return true;
  }, []);

  // Get display name for the current tier/plan
  const getTierDisplayName = useCallback(() => {
    if (isTrialing) {
      return `${subscription.plan.charAt(0).toUpperCase() + subscription.plan.slice(1)} (Trial)`;
    }
    switch (subscription.plan) {
      case PLAN_TYPES.FREE:
        return 'Free Plan';
      case PLAN_TYPES.PRO:
        return 'Pro Plan';
      case PLAN_TYPES.LEAGUE:
        return 'League Plan';
      default:
        return 'Free Plan';
    }
  }, [subscription.plan, isTrialing]);

  // Format period end date for display
  const periodEndFormatted = useMemo(() => {
    if (!subscription.currentPeriodEnd) return null;
    
    const endDate = subscription.currentPeriodEnd instanceof Date 
      ? subscription.currentPeriodEnd 
      : subscription.currentPeriodEnd.toDate?.() || new Date(subscription.currentPeriodEnd);
    
    return endDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }, [subscription.currentPeriodEnd]);

  const value = useMemo(() => ({
    // Subscription data
    subscription,
    loading,
    error,
    
    // Plan checks
    plan: subscription.plan,
    isPro,
    isLeague,
    isFree,
    
    // Status checks
    status: subscription.status,
    isActive,
    isTrialing,
    isPastDue,
    isCanceled,
    willCancelAtPeriodEnd,
    
    // Trial info
    trialDaysRemaining,
    isTrialExpiringSoon,
    
    // Limits
    currentLimits,
    
    // Helper functions
    canUseFeature,
    getRequiredPlan,
    isWithinLimit,
    
    // Legacy compatibility methods (used by Dashboard)
    hasActiveSubscription,
    canAccessLeagues,
    canAccessTournaments,
    getTierDisplayName,
    
    // Formatted dates
    periodEndFormatted,
    
    // Stripe IDs (for API calls)
    stripeCustomerId: subscription.stripeCustomerId,
    stripeSubscriptionId: subscription.stripeSubscriptionId,
  }), [
    subscription,
    loading,
    error,
    isPro,
    isLeague,
    isFree,
    isActive,
    isTrialing,
    isPastDue,
    isCanceled,
    willCancelAtPeriodEnd,
    trialDaysRemaining,
    isTrialExpiringSoon,
    currentLimits,
    canUseFeature,
    getRequiredPlan,
    isWithinLimit,
    hasActiveSubscription,
    canAccessLeagues,
    canAccessTournaments,
    getTierDisplayName,
    periodEndFormatted,
  ]);

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

// Custom hook to use subscription context
export function useSubscription() {
  const context = useContext(SubscriptionContext);
  
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  
  return context;
}

// Export constants for convenience
export { PLAN_TYPES, SUBSCRIPTION_STATUS, PLAN_LIMITS };
